import { useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { Scopes, User } from '../../../../../services/endpoints/people/user';
import { recipesColumns } from '../../../../../utils/consts/columnDefs/contentColumns';

import SFCTable from '../../../../../components/tables/SFCTable';
import { useGetRecipesQuery } from '../../../../../services/endpoints/content/recipe';
import { getCurrentUser } from '../../../../../services/helper';
import { Recipe } from '../../../../../types/lessonPlan.type';
import {
  cookMethods,
  cuisineTypes,
  mealTypes,
  seasonValues
} from '../../../../../utils/consts/content/recipes';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';

const defaultScope = 'all';

export const userCanEditCreateDelete_Recipes = (user: User | null) => {
  if (user?.isAdmin && user?.isSuper) {
    return true;
  }
  if (user?.isWebContentEditor || user?.isSupremeAdmin) {
    return true;
  }

  return false;
};

/** Hard-delete is incomplete (orphans child/join rows). Super/Supreme only until cleanup is fixed. */
export const userCanDelete_Recipes = (user: User | null) => {
  return !!user?.isSuper;
};

export const userCanView_Recipes = (user: User | null) => {
  if (user?.isInstructor) {
    return false;
  }

  return true;
};

export const RecipesView = () => {
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl({
    scope: defaultScope,
    data: { page: 1, entries: 10 }
  });

  const [scopes, setScopes] = useState<Scopes>({});
  const { data, error, isLoading, isFetching } = useGetRecipesQuery(filters);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const navigate: NavigateFunction = useNavigate();
  const currentUser = getCurrentUser();

  const seasonOptions = Object.entries(seasonValues).map((p) => ({
    value: p[0],
    label: p[1]
  }));
  const cuisineOptions = Object.entries(cuisineTypes).map((p) => ({
    value: p[0],
    label: p[1]
  }));
  const cookMethodOptions = Object.entries(cookMethods).map((p) => ({
    value: p[0],
    label: p[1]
  }));
  const mealTypeOptions = Object.entries(mealTypes).map((p) => ({
    value: p[0],
    label: p[1]
  }));

  const filterFormFields: FilterField[] = [
    {
      name: 'title',
      label: 'Title',
      value: getFilterValue(filters.data?.filterBy, 'title')
    },
    {
      name: 'author',
      label: 'Author',
      value: getFilterValue(filters.data?.filterBy, 'author')
    },
    {
      name: 'ingredient',
      label: 'Ingredient',
      value: getFilterValue(filters.data?.filterBy, 'ingredient')
    },
    {
      name: 'season',
      element: (
        <FormSelect
          name="season"
          label="Season"
          options={seasonOptions}
          selected_Value={seasonOptions.find(
            (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'season')
          )}
        />
      )
    },
    {
      name: 'Cuisine Type',
      element: (
        <FormSelect
          name="cuisine_type"
          label="Cuisine Type"
          options={cuisineOptions}
          selected_Value={cuisineOptions.find(
            (option) =>
              String(option.value) === getFilterValue(filters.data?.filterBy, 'cuisine_type')
          )}
        />
      )
    },
    {
      name: 'Cook Method',
      element: (
        <FormSelect
          name="cook_method"
          label="Cook Method"
          options={cookMethodOptions}
          selected_Value={cookMethodOptions.find(
            (option) =>
              String(option.value) === getFilterValue(filters.data?.filterBy, 'cook_method')
          )}
        />
      )
    },
    {
      name: 'Meal Type',
      element: (
        <FormSelect
          name="meal_type"
          label="Meal Type"
          options={mealTypeOptions}
          selected_Value={mealTypeOptions.find(
            (option) =>
              String(option.value) === getFilterValue(filters.data?.filterBy, 'meal_type')
          )}
        />
      )
    },
    {
      name: 'prep_time',
      label: 'Prep Time',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'prep_time')
    },
    {
      name: 'cook_time',
      label: 'Cook Time',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'cook_time')
    },
    {
      name: 'created_at_from',
      id: 'created_at',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="created_at_from"
          label="Created At"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'created_at', 'greater_equal_than')
    },
    {
      name: 'created_at_to',
      id: 'created_at',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="created_at_to" />,
      value: getFilterValue(filters.data?.filterBy, 'created_at', 'lesser_equal_than')
    }
  ];

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
          data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
          data.scopes.map((sMap) =>
            Object.entries(sMap).map(([scope, title]) => {
              let scope_count = 0;
              if (data.scope_counts != null) {
                scope_count = data.scope_counts[scope] ?? 0;
              }
              return {
                title: `${title} (${scope_count})`,
                key: scope
              };
            })
          )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setRecipes(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  return (
    <div className="py-2">
      <SFCTable
        name={`${scopes[filters?.scope!] ?? ''} Recipes`}
        columns={recipesColumns}
        data={recipes}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        scopes={buttons}
        isLoading={isLoading}
        isFetching={isFetching}
        indexDownloadPath="recipes"
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
        newItemEl={
          userCanEditCreateDelete_Recipes(currentUser) && (
            <SimpleButton
              variant="sfc-blueberry"
              height={40}
              truncate={false}
              onClick={() => navigate('/admin/content/recipes/new')}
            >
              New Recipe
            </SimpleButton>
          )
        }
      />
    </div>
  );
};
